/**
 * EntendaCalculo - Seção expansível com fatores, provas de credibilidade,
 * backtest real, cálculo da faixa, convergência, DRC, custos e fontes.
 */
import React, { useState } from 'react';

const fmtBRL = (v) => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;
const fmtPct = (v) => `${v > 0 ? '+' : ''}${String(v).replace('.', ',')}%`;

export const EntendaCalculo = ({ data, backtest }) => {
  const [open, setOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const { faixa, trend, confidence } = data;
  const f = trend.factors || {};

  return (
    <div className="bg-white rounded-2xl shadow">
      <button
        data-testid="trend-entenda-toggle"
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <span className="font-bold text-gray-800 text-sm">🔍 Entenda o cálculo e as provas</span>
        <span className="text-gray-400 text-xs">{open ? 'fechar ▴' : 'toque para abrir ▾'}</span>
      </button>

      {open && (
        <div data-testid="trend-entenda-content" className="px-4 pb-4 space-y-3">
          {/* Fatores */}
          <div className="border-l-4 border-indigo-400 bg-indigo-50/50 rounded-r-xl p-3">
            <p className="text-xs font-bold text-indigo-900">⚙️ O que está movendo a tendência agora</p>
            <div className="grid grid-cols-2 gap-2 mt-2 text-center">
              {f.tsr20?.value && (
                <div className="bg-white rounded-xl p-2 shadow-sm">
                  <p className="text-[10px] text-gray-500">TSR-20 (SGX, diário)</p>
                  <p className="text-sm font-black text-gray-800">US$ {f.tsr20.value.toFixed(2).replace('.', ',')}</p>
                  {f.tsr20.change_pct != null && (
                    <p className={`text-[10px] font-bold ${f.tsr20.change_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {f.tsr20.change_pct >= 0 ? '▲' : '▼'} {fmtPct(f.tsr20.change_pct)} desde {f.tsr20.since}
                    </p>
                  )}
                </div>
              )}
              {f.ptax?.value && (
                <div className="bg-white rounded-xl p-2 shadow-sm">
                  <p className="text-[10px] text-gray-500">Dólar PTAX (BCB, diário)</p>
                  <p className="text-sm font-black text-gray-800">R$ {f.ptax.value.toFixed(2).replace('.', ',')}</p>
                  {f.ptax.change_pct != null && (
                    <p className={`text-[10px] font-bold ${f.ptax.change_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {f.ptax.change_pct >= 0 ? '▲' : '▼'} {fmtPct(f.ptax.change_pct)} desde {f.ptax.since}
                    </p>
                  )}
                </div>
              )}
            </div>
            <p className="text-[11px] text-gray-600 mt-2">
              Outros fatores monitorados: clima na Ásia (produção), estoques na China e demanda de pneus.
            </p>
          </div>

          {/* Credibilidade + backtest real */}
          <div className="border border-gray-200 rounded-xl p-3">
            <p className="text-xs font-bold text-gray-800">🛡️ Como saber se essa tendência é real?</p>
            <ul className="text-[11px] text-gray-600 mt-1.5 space-y-1.5">
              <li><strong>1. Só ingredientes oficiais e públicos:</strong> TSR-20 (bolsa de Singapura/SGX), dólar PTAX (Banco Central) e referência CNA/IEA. Qualquer um pode conferir na fonte.</li>
              <li><strong>2. Método auditável:</strong> a referência CNA/IEA é, por construção, TSR-20 × câmbio + custos de internação. Acompanhamos os ingredientes todo dia — por isso antecipamos o resultado antes da publicação oficial.</li>
              <li><strong>3. Recalibração automática:</strong> a cada nova referência publicada, o fator de internação é recalculado ({trend.calibration_note}).</li>
            </ul>
            {confidence?.months_tested && (
              <div data-testid="trend-backtest-badge" className="flex items-center gap-2 mt-2.5 bg-green-50 border border-green-200 rounded-lg p-2">
                <span className="text-base">🎯</span>
                <p className="text-[11px] text-green-800">
                  <strong>Confiabilidade (backtest real, {confidence.window}):</strong> direção correta em{' '}
                  <strong>{confidence.direction_hits} de {confidence.months_tested} meses ({confidence.hit_rate_pct}%)</strong> · erro médio do valor: <strong>±{String(confidence.mape_pct).replace('.', ',')}%</strong>
                </p>
              </div>
            )}
            {backtest?.details?.length > 0 && (
              <>
                <button
                  data-testid="trend-backtest-table-toggle"
                  onClick={() => setShowTable(!showTable)}
                  className="text-[11px] text-green-700 font-semibold mt-2 underline"
                >
                  {showTable ? 'ocultar histórico de acertos' : 'ver histórico de acertos, mês a mês'}
                </button>
                {showTable && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-[10px] text-gray-600">
                      <thead>
                        <tr className="text-gray-400 border-b">
                          <th className="text-left py-1">Mês</th>
                          <th className="text-right">Projetado</th>
                          <th className="text-right">Publicado</th>
                          <th className="text-right">Erro</th>
                          <th className="text-center">Direção</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtest.details.map((d) => (
                          <tr key={d.month} className="border-b border-gray-100">
                            <td className="py-1">{d.month}</td>
                            <td className="text-right">{fmtBRL(d.predicted)}</td>
                            <td className="text-right">{fmtBRL(d.actual)}</td>
                            <td className="text-right">{String(d.error_pct).replace('.', ',')}%</td>
                            <td className="text-center">{d.hit ? '✅' : '❌'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[9px] text-gray-400 mt-1">{confidence?.method}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Como a faixa é calculada */}
          <div className="border-l-4 border-green-500 bg-green-50/50 rounded-r-xl p-3">
            <p className="text-xs font-bold text-green-900">🧮 Como a faixa é calculada</p>
            <p className="text-[11px] text-gray-600 mt-1">
              Referência {data.faixa.reference_current.label} ({fmtBRL(faixa.reference_current.value)}) × DRC {faixa.drc_pct}% ={' '}
              <strong>{fmtBRL(faixa.reference_current.value * faixa.drc_pct / 100)}</strong> é o valor "cheio" do coágulo.
              O produtor historicamente recebe uma participação disso: <strong>{faixa.justo_pct}% é o piso histórico de participação</strong> ({fmtBRL(faixa.justo)})
              e <strong>{String(faixa.teto_pct).replace('.', ',')}% foi o teto</strong> já registrado ({fmtBRL(faixa.teto)}).
              {faixa.participation_pct != null && <> Hoje o mercado paga <strong>{String(faixa.participation_pct).replace('.', ',')}%</strong>.</>}
            </p>
          </div>

          {/* Convergência */}
          <div className="border-l-4 border-purple-500 bg-purple-50/50 rounded-r-xl p-3">
            <p className="text-xs font-bold text-purple-900">🔮 O padrão de convergência (o elo que depende da usina)</p>
            <p className="text-[11px] text-gray-600 mt-1">
              A participação do produtor sempre volta para a média de <strong>70–72%</strong>. Nas últimas vezes que ficou perto do piso (52%) com a referência subindo,
              o coágulo corrigiu em 2–4 meses (ex.: jan/25, <strong>+40% em 2 meses</strong>). A tendência acima diz para onde vai a <em>referência</em>;
              a convergência diz o que falta o mercado repassar. Padrão histórico ≠ garantia. Série IEA 2020–2026.
            </p>
          </div>

          {/* DRC */}
          <div className="border-l-4 border-teal-500 bg-teal-50/50 rounded-r-xl p-3">
            <p className="text-xs font-bold text-teal-900">⚖️ Cuidado com a pegadinha do DRC</p>
            <p className="text-[11px] text-gray-600 mt-1">
              Compare sempre por <strong>kg seco</strong> (preço ÷ DRC). Exemplo (jan/26): R$ 3,76 a DRC 53% = <strong>R$ 7,09/kg seco</strong> ✅ paga MAIS
              que R$ 3,80 a DRC 65% = R$ 5,85/kg seco. Use a aba 🧮 Calculadora para comparar ofertas em 2 toques.
            </p>
          </div>

          {/* Custos e líquido */}
          <div className="border-l-4 border-amber-500 bg-amber-50/50 rounded-r-xl p-3">
            <p className="text-xs font-bold text-amber-900">💼 Custos e líquido no bolso</p>
            <p className="text-[11px] text-gray-600 mt-1">
              Custo médio regional (FAESP, Rio Preto, safra 2025/26): COT ~R$ 5,77/kg — veja os custos vigentes na aba 📊 Análise.
              {faixa.market?.value && faixa.market_liquido_funrural && (
                <> Vendendo a {fmtBRL(faixa.market.value)}, o líquido após Funrural (1,5%) é <strong>~{fmtBRL(faixa.market_liquido_funrural)}/kg</strong>.</>
              )}
              {' '}Proprietários: compare com o custo do seu Fechamento de Safra.
            </p>
          </div>

          {/* Fontes */}
          <div className="bg-gray-100 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 font-semibold">FONTES (todas públicas e verificáveis)</p>
            <p className="text-[10px] text-gray-500 mt-1">
              TSR-20: Trading Economics / SGX Singapura e World Bank Pink Sheet (médias mensais) · Dólar: PTAX Banco Central (bcb.gov.br) ·
              Referência: CNA / IEA-SP · Mínimo: CONAB (PGPM {faixa.conab_min?.ref}) · Custos: FAESP · Preço recebido: IEA (levantamento mensal)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
